"use client"

import { useState, useEffect, useCallback } from "react"
import {
  FaEdit,
  FaPlus,
  FaUsers,
  FaSearch,
  FaCheck,
  FaTimes,
  FaTrash,
  FaBan,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa"

import AddUser from "./popups/AddUser"
import Deleteuser from "./popups/Deleteuser"
import DeactivateUser from "./popups/DeactivateUser"
import ActivateUser from "./popups/ActivateUser"
import ModifierUser from "./popups/ModifierUser"

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`
const CACHE_DURATION = 5 * 60 * 1000; 

const TableUser = () => {
  const [usersData, setUsersData] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [retryCount, setRetryCount] = useState(0)

  // POPUPS STATE
  const [openAdduser, setOpenAdduser] = useState(false)
  const [openModuser, setOpenModuser] = useState(false)
  const [openDeleteuser, setOpenDeleteuser] = useState(false)
  const [openDeactivateUser, setOpenDeactivateUser] = useState(false)
  const [openActivateUser, setOpenActivateUser] = useState(false)

  const [selectedUser, setSelectedUser] = useState(null)
  const [roleFilter, setRoleFilter] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")

  // Récupérer le token
  const getToken = () => localStorage.getItem("access_token")

  // Cache helpers
  const getCachedRoles = useCallback(() => {
    try {
      const cached = localStorage.getItem('cached_roles');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (e) {
      console.warn("Erreur de cache:", e);
    }
    return null;
  }, []);

  const setCachedRoles = useCallback((data) => {
    try {
      localStorage.setItem('cached_roles', JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn("Erreur de sauvegarde cache:", e);
    }
  }, []);

  // Charger les rôles 
  const fetchRoles = useCallback(async (force = false) => {
    if (!force) {
      const cached = getCachedRoles();
      if (cached) {
        console.log("📦 Rôles chargés depuis le cache");
        setRoles(cached);
        return cached;
      }
    }

    try {
      const token = getToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`${API_URL}/roles/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log("🌐 Rôles chargés depuis le serveur");
        
        let rolesData = [];
        if (data.results) rolesData = data.results;
        else if (Array.isArray(data)) rolesData = data;
        
        setRoles(rolesData);
        setCachedRoles(rolesData);
        return rolesData;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn("Timeout chargement rôles");
      } else {
        console.error("Error fetching roles:", error);
      }
    }
    return null;
  }, [getCachedRoles, setCachedRoles]);

  // Charger les utilisateurs
  const fetchUsers = useCallback(async () => {
    try {
      const token = getToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${API_URL}/users/?page_size=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log("🌐 Utilisateurs chargés");
        
        let users = [];
        if (data.results) users = data.results;
        else if (data.data) users = data.data;
        else if (Array.isArray(data)) users = data;
        
        setUsersData(users);
        setError("");
        setRetryCount(0);
      } else if (response.status === 401) {
        setError("Session expirée, veuillez vous reconnecter");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError("Le chargement prend trop de temps. Vérifie ta connexion.");
      } else {
        console.error("Error fetching users:", error);
        setError("Erreur de chargement des données");
      }
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setError("Vous devez être connecté");
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      
      await fetchRoles();
      await fetchUsers();
      
      setLoading(false);
    };

    loadData();
  }, [fetchRoles, fetchUsers]);

  // DELETE USER
  const handleDeleteUser = async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/users/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setUsersData(prev => prev.filter(u => u.id !== id));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    }
    setOpenDeleteuser(false);
    setSelectedUser(null);
  }

  // DEACTIVATE USER
  const handleDeactivateUser = async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/users/${id}/toggle_active/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setUsersData(prev =>
          prev.map(u =>
            u.id === id ? { ...u, is_active: false } : u
          )
        );
      }
    } catch (error) {
      console.error("Error toggling user:", error);
    }
    setOpenDeactivateUser(false);
    setSelectedUser(null);
  }

  // ACTIVATE USER
  const handleActivateUser = async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/users/${id}/toggle_active/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setUsersData(prev =>
          prev.map(u =>
            u.id === id ? { ...u, is_active: true } : u
          )
        );
      }
    } catch (error) {
      console.error("Error activating user:", error);
    }
    setOpenActivateUser(false);
    setSelectedUser(null);
  }

  // Filtrage
  const filteredUsers = usersData
    .filter((u) => roleFilter === "All" || u.role_name === roleFilter || u.role?.name === roleFilter)
    .filter(
      (u) =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getRoleColor = (role) => {
    const roleName = role?.name || role || "Aucun";
    const colors = {
      Admin: "bg-red-100 text-red-800 border-red-200",
      Manager: "bg-purple-100 text-purple-800 border-purple-200",
      Employé: "bg-indigo-100 text-indigo-800 border-indigo-200",
    };
    return colors[roleName] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getRoleName = (user) => {
    return user.role_name || user.role?.name || "Aucun";
  };

  // Retry
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchUsers();
    fetchRoles(true); 
  };

  if (loading) return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <FaSpinner className="animate-spin text-4xl text-[#5E17EB] mx-auto mb-4" />
        <p className="text-gray-600">Chargement des utilisateurs...</p>
        <p className="text-sm text-gray-400 mt-2">Si le chargement persiste, vérifie ta connexion</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg text-center">
        <FaExclamationTriangle className="inline mr-2 text-xl" />
        {error}
        <button 
          onClick={handleRetry}
          className="ml-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Réessayer
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      <h1 style={styles.title}>LISTE DES UTILISATEURS</h1>
      <p style={styles.subtitle}>
        Gérez les utilisateurs et leurs permissions
      </p>

      <div className="bg-white rounded-xl shadow-lg border mb-10">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-[#56C596] to-[#5E17EB] px-6 py-5 flex justify-between items-center">
          <div className="flex items-center text-white">
            <FaUsers size={22} />
            <h2 className="ml-3 text-xl font-bold">
              Gestion des Utilisateurs ({usersData.length})
            </h2>
          </div>

          <button
            onClick={() => setOpenAdduser(true)}
            className="bg-white text-[#5E17EB] px-5 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <FaPlus className="inline mr-2" />
            Ajouter utilisateur
          </button>
        </div>

        {/* FILTER */}
        <div className="bg-[#F3EEFF] p-4 flex gap-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              className="pl-10 p-2 w-full rounded-md border focus:outline-none focus:ring-2 focus:ring-[#5E17EB]"
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="p-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#5E17EB] min-w-[150px]"
          >
            <option value="All">Tous les rôles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.name}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                {["Utilisateur", "Rôle", "Email", "Statut", "Actions"].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase text-center"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-[#F3EEFF] transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-[#5E17EB] to-[#7c3aed] text-white flex items-center justify-center font-bold">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full border text-sm ${getRoleColor(getRoleName(user))}`}>
                        {getRoleName(user)}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-center">{user.email}</td>

                    <td className="px-6 py-4 text-center">
                      {user.is_active ? (
                        <span className="text-green-600 font-medium flex items-center justify-center gap-1">
                          <FaCheck className="inline" /> Actif
                        </span>
                      ) : (
                        <span className="text-gray-500 font-medium flex items-center justify-center gap-1">
                          <FaTimes className="inline" /> Inactif
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        {/* EDIT */}
                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setOpenModuser(true)
                          }}
                          className="bg-[#EDE9FF] text-[#5E17EB] p-2 rounded-full hover:bg-[#5E17EB] hover:text-white transition-colors"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        {/* ACTIVER/DÉSACTIVER */}
                        {user.is_active ? (
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setOpenDeactivateUser(true)
                            }}
                            className="bg-yellow-100 text-yellow-600 p-2 rounded-full hover:bg-yellow-600 hover:text-white transition-colors"
                            title="Désactiver"
                          >
                            <FaBan />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setOpenActivateUser(true)
                            }}
                            className="bg-green-100 text-green-600 p-2 rounded-full hover:bg-green-600 hover:text-white transition-colors"
                            title="Activer"
                          >
                            <FaCheck />
                          </button>
                        )}

                        {/* DELETE */}
                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setOpenDeleteuser(true)
                          }}
                          className="bg-red-100 text-red-600 p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer avec stats */}
        <div className="bg-gray-50 px-6 py-3 border-t">
          <p className="text-sm text-gray-600">
            Affichage {filteredUsers.length} sur {usersData.length} utilisateurs
          </p>
        </div>
      </div>

      {/* POPUPS */}
      {openAdduser && (
        <AddUser 
          closeAdduser={setOpenAdduser} 
          setUsersData={setUsersData}
          roles={roles}
        />
      )}
      
      {openModuser && (
        <ModifierUser 
          closeModuser={setOpenModuser} 
          selectedUser={selectedUser}
          roles={roles}
          setUsersData={setUsersData}
        />
      )}

      {openDeleteuser && (
        <Deleteuser
          closeDeleteuser={setOpenDeleteuser}
          handleDelete={handleDeleteUser}
          selectedUser={selectedUser}
        />
      )}

      {openDeactivateUser && (
        <DeactivateUser
          closeDeactivateUser={setOpenDeactivateUser}
          handleDeactivate={handleDeactivateUser}
          selectedUser={selectedUser}
        />
      )}

      {openActivateUser && (
        <ActivateUser
          closeActivateUser={setOpenActivateUser}
          handleActivate={handleActivateUser}
          selectedUser={selectedUser}
        />
      )}
    </div>
  )
}

export default TableUser

const styles = {
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500", 
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
}